import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls,
  useNodesState, useEdgesState, addEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  BusNode, edgeStyle, NODE_STYLE, SidePanel, Toolbar, ToolbarBtn, ToolbarDivider,
} from "./flow/primitives";
import { WarningOctagon, Circuitry } from "@phosphor-icons/react";

/* v2.95 · TCC Coordination graphical editor.
   Represents the protection chain as a top-to-bottom SLD:
     [fault source] → device_1 (upstream) → device_2 → … → device_N (downstream)
   Emits {devices: […], fault_current_amps, check_coordination_seconds}. */

const DEVICE_STYLE = {
  colour: "#7CFF6B",
  badge:  "device",
  icon:   Circuitry,
};
const FAULT_STYLE = {
  colour: "#FF3B30",
  badge:  "fault",
  icon:   WarningOctagon,
};

const FaultNode  = (p) => <BusNode {...p} {...FAULT_STYLE}  sub={(d) => `${d.fault_current_amps} A · gap ${d.check_coordination_seconds}s`} />;
const DeviceNode = (p) => <BusNode {...p} {...DEVICE_STYLE} sub={(d) => `pickup ${d.pickup_amps}A · TD ${d.time_dial} · ${d.curve}`} />;
const nodeTypes = { FAULT: FaultNode, DEVICE: DeviceNode };

const INITIAL_NODES = [
  { id: "F0",  type: "FAULT",  position: { x: 260, y: 380 },
    data: { id: "F0", fault_current_amps: 2000, check_coordination_seconds: 0.3 } },
  { id: "D1", type: "DEVICE", position: { x: 260, y: 240 },
    data: { id: "D1", tag: "51-DS", pickup_amps: 100, time_dial: 2, curve: "MI" } },
  { id: "D2", type: "DEVICE", position: { x: 260, y: 100 },
    data: { id: "D2", tag: "51-US", pickup_amps: 200, time_dial: 5, curve: "MI" } },
];
const INITIAL_EDGES = [
  { id: "e1", source: "F0", target: "D1", ...edgeStyle() },
  { id: "e2", source: "D1", target: "D2", ...edgeStyle() },
];

const FIELDS = {
  FAULT: [
    { key: "id",                           label: "Tag",             type: "text" },
    { key: "fault_current_amps",           label: "Fault (A)",       type: "number" },
    { key: "check_coordination_seconds",   label: "Min gap (s)",     type: "number", step: "0.05" },
  ],
  DEVICE: [
    { key: "tag",           label: "Tag",         type: "text" },
    { key: "pickup_amps",   label: "Pickup (A)",  type: "number" },
    { key: "time_dial",     label: "Time dial",   type: "number", step: "0.1" },
    { key: "curve",         label: "Curve",       type: "select",
      options: [{ value: "MI", label: "moderately inverse" },
                { value: "VI", label: "very inverse" },
                { value: "EI", label: "extremely inverse" }] },
  ],
};

export function graphToTcc(nodes) {
  const fault = nodes.find((n) => n.type === "FAULT")?.data || {};
  // Order devices by y position — smaller y = upstream (higher on screen)
  const devs = nodes.filter((n) => n.type === "DEVICE")
    .sort((a, b) => b.position.y - a.position.y)     // downstream first
    .map((n) => ({
      tag:           n.data.tag || n.data.id,
      pickup_amps:   Number(n.data.pickup_amps || 100),
      time_dial:     Number(n.data.time_dial || 2),
      curve:         n.data.curve || "MI",
    }));
  return {
    devices:                        devs,
    fault_current_amps:             Number(fault.fault_current_amps || 2000),
    check_coordination_seconds:     Number(fault.check_coordination_seconds || 0.3),
  };
}

function TCCSldInner({ onChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(INITIAL_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(INITIAL_EDGES);
  const [sel, setSel] = useState({ node: null, edge: null });
  const idCounter = useRef(10);

  useEffect(() => { onChange?.(graphToTcc(nodes)); }, [nodes, onChange]);

  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    setEdges((es) => addEdge({ ...c, id: `e${idCounter.current++}`, ...edgeStyle() }, es));
  }, [setEdges]);

  const addDevice = () => {
    const id = `D${idCounter.current++}`;
    setNodes((ns) => [...ns, {
      id, type: "DEVICE",
      position: { x: 260, y: 40 + Math.random() * 60 },
      data: { id, tag: id, pickup_amps: 150, time_dial: 3, curve: "MI" },
    }]);
  };
  const clearAll = () => {
    setNodes([INITIAL_NODES[0]]);          // keep the fault node
    setEdges([]); setSel({ node: null, edge: null });
  };

  const onSelectionChange = useCallback(({ nodes: ns, edges: es }) =>
    setSel({ node: ns[0] || null, edge: es[0] || null }), []);

  const patchNode = (key, value) => {
    if (!sel.node) return;
    setNodes((all) => all.map((n) => n.id === sel.node.id
      ? { ...n, data: { ...n.data, [key]: value } } : n));
    setSel((s) => ({ ...s, node: { ...s.node, data: { ...s.node.data, [key]: value } } }));
  };
  const deleteSel = () => {
    if (sel.node && sel.node.type !== "FAULT") {
      const id = sel.node.id;
      setNodes((ns) => ns.filter((n) => n.id !== id));
      setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    } else if (sel.edge) {
      const id = sel.edge.id;
      setEdges((es) => es.filter((e) => e.id !== id));
    }
    setSel({ node: null, edge: null });
  };

  return (
    <div className="h-[520px] border border-white/10 bg-[#0A0C10] relative" data-testid="tcc-sld-canvas">
      <Toolbar>
        <ToolbarBtn colour="#7CFF6B" onClick={addDevice} testid="tcc-add-device" title="Add relay / breaker">
          <Plus size={10} className="inline" /> device
        </ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn colour="#FF3B30" onClick={deleteSel} testid="tcc-delete-selected" title="Delete selected">
          <Trash size={10} className="inline" /> delete
        </ToolbarBtn>
        <ToolbarBtn colour="#8792A2" onClick={clearAll} testid="tcc-clear" title="Reset (keeps fault)">reset</ToolbarBtn>
      </Toolbar>

      <ReactFlow
        nodes={nodes} edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onSelectionChange={onSelectionChange}
        fitView
        proOptions={{ hideAttribution: true }}
        colorMode="dark"
      >
        <Background gap={16} color="#ffffff08" />
        <Controls className="!bg-[#12151C] !border-white/10" showInteractive={false} />
      </ReactFlow>

      {sel.node && (
        <SidePanel
          title={sel.node.data.tag || sel.node.data.id}
          subtitle={sel.node.type === "FAULT" ? "fault source" : "protective device"}
          node={sel.node}
          fields={FIELDS[sel.node.type] || []}
          onChange={patchNode}
          onDelete={sel.node.type !== "FAULT" ? deleteSel : undefined}
          onClose={() => setSel({ node: null, edge: null })}
        />
      )}
    </div>
  );
}

export default function TCCSLD({ onChange }) {
  return (
    <ReactFlowProvider>
      <TCCSldInner onChange={onChange} />
    </ReactFlowProvider>
  );
}
